import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';

import {
  DialogContent, Grid, FormControlLabel, Checkbox
} from '@mui/material';

import { translate } from 'utils';

import {
  CustomIcon, FormElement
} from 'components';

import { faLanguage, faPen } from '@fortawesome/pro-solid-svg-icons';
import { HeaderModal } from './_HeaderModal';
import { CustomActionButtons } from './_CustomActionButtons';

const UpdateUserForm = styled.form`
  .MuiToggleButton-root,
  .MuiToggleButton-root:hover {
    background-color: var(--white);
  }

  .MuiToggleButton-root.Mui-selected:first-child,
  .MuiToggleButton-root.Mui-selected:first-child:hover {
    background-color: var(--success-color);
  }

  .MuiToggleButton-root.Mui-selected:last-child,
  .MuiToggleButton-root.Mui-selected:last-child:hover {
    background-color: var(--error-color);
  }

  @media(min-width: 768px) {
    width: 600px;
  }
`;

export const UpdateRoleLanguagesModal = ({
                                           onClose, onConfirm, closeOnSubmit, languages, defaultLanguages
                                         }) => {
  const {
 register, handleSubmit, setValue, watch
} = useForm({
    defaultValues: {
      locales: defaultLanguages.map(lng => lng.locale) || []
    }
  });

  const [allLanguages, setAllLanguages] = useState([]);

  // Register the locales field
  useEffect(() => {
    register('locales');
  }, [register]);

  useEffect(() => {
    setAllLanguages(languages);
  }, [languages]);

  // Get current form values
  const formValues = watch();

  const getSelectedLanguages = useCallback(selectedLocales => (
      selectedLocales.map(locale => {
        const fetchedLanguage = allLanguages.find(lng => lng.locale === locale);
        return { ...fetchedLanguage };
      })
  ), [allLanguages]);

  const onSubmit = useCallback(data => {
    onConfirm(getSelectedLanguages(data.locales));
    if (closeOnSubmit) {
      onClose();
    }
  }, [closeOnSubmit, onClose, onConfirm, getSelectedLanguages]);

  // Handle the checkbox change
  const handleCheckboxChange = useCallback((locale, event) => {
    const isChecked = event.target.checked;
    const currentLocales = formValues.locales || [];

    if (isChecked) {
      // Add locale if checked
      setValue('locales', [...currentLocales, locale]);
    } else {
      // Remove locale if unchecked
      setValue('locales', currentLocales.filter(item => item !== locale));
    }
  }, [formValues.locales, setValue]);

  // Check if a locale is selected
  const isLocaleSelected = useCallback((locale) => (formValues.locales || []).includes(locale), [formValues.locales]);

  return (
    <UpdateUserForm onSubmit={handleSubmit(onSubmit)}>
      <HeaderModal onClose={onClose}>
        <Grid alignItems="center" container>
          <Grid item>
            <CustomIcon icon={faLanguage} secondIcon={faPen} />
          </Grid>
          <Grid item>
            {translate('modalUserUpdate.editLanguages')}
          </Grid>
        </Grid>
      </HeaderModal>

      <DialogContent style={{ paddingTop: '10px' }}>
        <FormElement label={translate('pageLanguages.title')}>
          <Grid container direction="column">
            {/* ROLES CHECKBOX MULTIPLE */}
            {allLanguages && allLanguages.map(language => (
              <Grid container item justifyContent="space-between" key={language.locale} spacing={2}>
                <Grid item>
                  <FormControlLabel
                          control={
                            <Checkbox
                                checked={isLocaleSelected(language.locale)}
                                color="primary"
                                value={language.locale}
                                onChange={(e) => handleCheckboxChange(language.locale, e)}
                            />
                          }
                          key={language.locale}
                          label={translate(`languages.${language.locale}`)}
                      />
                </Grid>
              </Grid>
              ))}
          </Grid>
        </FormElement>
      </DialogContent>

      <CustomActionButtons
            isValidated
            onClose={onClose}
        />
    </UpdateUserForm>
  );
};

UpdateRoleLanguagesModal.propTypes = {
  closeOnSubmit: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultLanguages: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

UpdateRoleLanguagesModal.defaultProps = {
  closeOnSubmit: true
};
